import { useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarCheck2 } from 'lucide-react';
import type { MyEventDto } from '@fas/types';
import { Button, Card, CardContent, Dialog, DialogContent, StatusBadge } from '@fas/ui';
import { clockTime } from '@/features/attendance/labels';
import { CheckInDialog } from './CheckInDialog';
import { EVENT_ATTENDANCE, EVENT_STATUS, EVENT_TYPE, eventPlace, eventWhen } from './labels';

/** The caller's invitations with their own attendance and a check-in button while it is open. */
export function MyEventsList({ items, timeZone, emptyText }: { items: MyEventDto[]; timeZone: string; emptyText?: string }) {
  const queryClient = useQueryClient();
  const [checking, setChecking] = useState<MyEventDto | null>(null);
  if (items.length === 0) return emptyText ? <p className="text-sm text-slate-600">{emptyText}</p> : null;
  return (
    <>
      <ul className="space-y-3">
        {items.map((item) => {
          const { event, attendance, checkIn } = item;
          const status = attendance ? EVENT_ATTENDANCE[attendance.status] : null;
          return (
            <li key={event.id}>
              <Card>
                <CardContent className="flex flex-col gap-3 pt-4 sm:flex-row sm:items-center sm:justify-between sm:pt-6" >
                  <article aria-label={event.title} className="space-y-1">
                    <p className="flex items-center gap-2 font-semibold">
                      <CalendarCheck2 className="size-4 text-blue-700" aria-hidden />
                      <Link to={`/events/${event.id}`} className="hover:underline">
                        {event.title}
                      </Link>
                    </p>
                    <p className="text-sm text-slate-600">
                      {EVENT_TYPE[event.eventType]} · {eventWhen(event, timeZone)} · {eventPlace(event)}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {event.status !== 'PUBLISHED' ? <StatusBadge label={EVENT_STATUS[event.status].label} tone={EVENT_STATUS[event.status].tone} /> : null}
                      {status ? <StatusBadge label={status.label} tone={status.tone} /> : null}
                      {event.attendanceRequired ? null : <StatusBadge label="Optional" tone="neutral" />}
                      {attendance?.checkedInAt ? <span className="text-xs text-slate-600">Checked in at {clockTime(attendance.checkedInAt, timeZone)}</span> : null}
                    </div>
                    {checkIn.hint && !attendance?.checkedInAt ? <p className="text-xs text-slate-600">{checkIn.hint}</p> : null}
                  </article>
                  {checkIn.canCheckIn ? (
                    checkIn.requiresScan ? (
                      <p className="max-w-56 text-sm text-slate-700">Scan the code at the venue with your phone’s camera to check in.</p>
                    ) : (
                      <Button onClick={() => setChecking(item)} aria-label={`Check in to ${event.title}`}>
                        Check in
                      </Button>
                    )
                  ) : null}
                </CardContent>
              </Card>
            </li>
          );
        })}
      </ul>
      <Dialog open={checking !== null} onOpenChange={(open) => (open ? null : setChecking(null))}>
        {checking ? (
          <DialogContent title="Check in">
            <CheckInDialog
              item={checking}
              timeZone={timeZone}
              onDone={() => void queryClient.invalidateQueries({ queryKey: ['/events'] })}
              onClose={() => setChecking(null)}
            />
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
}
